import { useState } from "react";

/** Editor for the DB-backed audience segments that replaced the old hardcoded
 *  {"PE Sponsor","Board Member"} → pe_board / everything-else → leadership split.
 *  Each audience owns a list of participant designations that route to it; the
 *  default audience is the catch-all for anything unmatched. */
export default function AudienceManagerModal({ audiences, saving, onSave, onCreate, onClose }) {
  const [drafts, setDrafts] = useState(() =>
    audiences.map((a) => ({ ...a, designationsText: (a.match_designations || []).join(", ") }))
  );
  const [newAudience, setNewAudience] = useState({ key: "", label: "", designationsText: "" });
  const [creating, setCreating] = useState(false);

  const parseDesignations = (text) =>
    text
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Audiences</h2>
        <p className="text-sm text-slate-500 mb-5">
          Participants are routed to an audience by their designation. Anything that matches no rule
          falls into the default audience.
        </p>

        <div className="space-y-3">
          {drafts.map((a, i) => (
            <div key={a.id} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center gap-2 mb-2">
                <input
                  className="flex-1 text-sm font-semibold text-gray-800 border-0 border-b border-transparent focus:border-slate-300 focus:outline-none bg-transparent px-0 py-0.5"
                  value={a.label}
                  onChange={(e) =>
                    setDrafts((d) => d.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                  }
                />
                <code className="text-[11px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                  {a.key}
                </code>
                {a.is_default ? (
                  <span className="text-[11px] font-semibold text-[#004f73] bg-[#004f73]/10 px-2 py-0.5 rounded-full shrink-0">
                    Default
                  </span>
                ) : null}
              </div>
              {a.is_default ? (
                <p className="text-xs text-slate-400">
                  Catch-all — receives every participant not matched by another audience.
                </p>
              ) : (
                <>
                  <label className="block text-[11px] font-medium text-slate-500 mb-0.5">
                    Designations (comma separated)
                  </label>
                  <input
                    className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#004f73]/20"
                    value={a.designationsText}
                    placeholder="Manager, Contributor"
                    onChange={(e) =>
                      setDrafts((d) =>
                        d.map((x, j) => (j === i ? { ...x, designationsText: e.target.value } : x))
                      )
                    }
                  />
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-3">
          <p className="text-sm font-semibold text-gray-800 mb-2">Add audience</p>
          <div className="flex gap-2 mb-2">
            <input
              className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
              placeholder="Label (e.g. Regional Leads)"
              value={newAudience.label}
              onChange={(e) => setNewAudience((n) => ({ ...n, label: e.target.value }))}
            />
            <input
              className="w-40 border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-mono"
              placeholder="key_name"
              value={newAudience.key}
              onChange={(e) =>
                setNewAudience((n) => ({
                  ...n,
                  key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                }))
              }
            />
          </div>
          <input
            className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mb-2"
            placeholder="Designations, comma separated"
            value={newAudience.designationsText}
            onChange={(e) => setNewAudience((n) => ({ ...n, designationsText: e.target.value }))}
          />
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium text-[#004f73] border border-[#004f73]/30 rounded-lg hover:bg-[#004f73]/5 disabled:opacity-40"
            disabled={!newAudience.key || !newAudience.label || creating}
            onClick={async () => {
              setCreating(true);
              try {
                const created = await onCreate({
                  key: newAudience.key,
                  label: newAudience.label,
                  match_designations: parseDesignations(newAudience.designationsText),
                });
                if (created) {
                  setDrafts((d) => [
                    ...d,
                    { ...created, designationsText: (created.match_designations || []).join(", ") },
                  ]);
                  setNewAudience({ key: "", label: "", designationsText: "" });
                }
              } finally {
                setCreating(false);
              }
            }}
          >
            {creating ? "Adding…" : "Add audience"}
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 pt-5">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg"
            onClick={onClose}
            disabled={saving}
          >
            Close
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg disabled:opacity-50"
            disabled={saving}
            onClick={() =>
              onSave(
                drafts.map((d) => ({
                  id: d.id,
                  label: d.label,
                  match_designations: d.is_default ? undefined : parseDesignations(d.designationsText),
                }))
              )
            }
          >
            {saving ? "Saving…" : "Save audiences"}
          </button>
        </div>
      </div>
    </div>
  );
}
